import { type ComponentTFunction } from '@pkgs/components/providers/AppContextProvider';
import { PropertyMarketingType, type SearchListingsFilters, type SearchListingsOptions } from '@ev-tech/search-modules-api';

import { getLifestyleCategoryTranslationKey } from './translationKeyUtils';
import { safeLocalStorage } from './storageUtils';
import { getSurfaceLabel, getPropertyLabel, getPriceLabel, mapFilterAttributesToString, getRoomsLabel } from './searchAlertUtils';
import { SearchMode } from '../hooks/stores/search/slices/searchModeSlice';
import { DEFAULT_PROPERTY_MARKETING_TYPE } from '../hooks/stores/search/slices/objectTypeFilterSlice';

export const USER_SEARCH_DETAILS_STORAGE_KEY = 'expose-search-context';

export function writeUserSearchDetails(details: string | undefined): void {
  const trimmedDetails = details?.trim();
  if (!trimmedDetails) {
    safeLocalStorage.removeItem(USER_SEARCH_DETAILS_STORAGE_KEY);
    return;
  }

  safeLocalStorage.setItem(USER_SEARCH_DETAILS_STORAGE_KEY, trimmedDetails);
}

export function formatUserSearchDetails({
  searchMode,
  prompt,
  filters,
  placeName,
  t,
  options,
}: {
  searchMode: SearchMode;
  prompt?: string;
  filters: SearchListingsFilters;
  placeName: string;
  t: ComponentTFunction;
  options: SearchListingsOptions;
}): string | undefined {
  const {
    propertyMarketingType,
    propertyTypeSubType,
    price,
    livingSurface,
    rooms,
    lifestyleCategory,
    placeIds: _placeIds,
    preferences: _preferences,
    businessArea,
    ...rest
  } = filters;

  const { language, currency } = options;
  const marketingType = propertyMarketingType[0] ?? DEFAULT_PROPERTY_MARKETING_TYPE;
  const marketingLabel = marketingType === PropertyMarketingType.rent ? t('search.searchbar.rent') : t('search.searchbar.sale');
  const propertyLabels = getPropertyLabel(t, propertyTypeSubType, businessArea[0]);
  const priceLabel = getPriceLabel({
    price,
    propertyMarketingType: propertyMarketingType[0] ?? DEFAULT_PROPERTY_MARKETING_TYPE,
    t,
    currency,
    language,
  });
  const roomsLabel = getRoomsLabel(filters, language, t);
  const livingAreaLabel = getSurfaceLabel({
    surfaceValue: livingSurface,
    type: 'livingSurface',
    t,
    options,
  });
  const restFilterAttributes = mapFilterAttributesToString({
    filters: rest,
    t,
    options,
  });
  const lifestyleLabels = (lifestyleCategory ?? []).flatMap((category) => {
    const translationKey = getLifestyleCategoryTranslationKey(category);
    return translationKey ? [t(translationKey)] : [];
  });

  const filterParts = [
    placeName,
    marketingLabel,
    ...propertyLabels,
    priceLabel,
    roomsLabel,
    livingAreaLabel,
    ...restFilterAttributes,
    ...lifestyleLabels,
  ].filter(Boolean);
  const filterSummary = filterParts.length > 0 ? filterParts.join(', ') : undefined;

  if (searchMode !== SearchMode.AI) {
    return filterSummary;
  }

  const summaryParts = [prompt?.trim(), filterSummary].filter(Boolean);

  return summaryParts.length > 0 ? summaryParts.join(', ') : undefined;
}
